import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgClass } from "@angular/common";
import { Component, Inject, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from "@angular/material/dialog";
import { MatOption, MatSelect } from "@angular/material/select";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ITeam } from "@teammood/team";
import { Author, Kudos, KudosRecipient, KudosRecipients, KudosType } from "@teammood/timeline";
import { debounceTime, distinctUntilChanged, map, mergeMap, Observable, Subject, takeUntil, tap } from "rxjs";
import {
    KudosRecipientsInputListComponent
} from "../kudos-recipients-input-list/kudos-recipients-input-list.component";
import { TmMoodPickersComponent } from "../../../../../shared/ui/moodpickers/mood-pickers.component";
import { UserActivityService } from "../../../../user-activity/user-activity.service";
import { STEPPER_GLOBAL_OPTIONS, StepperOrientation } from '@angular/cdk/stepper';
import { MatStepperModule } from '@angular/material/stepper';
import { KudosPreviewComponent } from '../kudos-preview/kudos-preview.component';
import { TemplateListComponent } from '../../../../timeline-item-creation-dialog/template-list/template-list.component';
import { UserInfos } from '../../../../../shared/ui/username-icon/user-infos.type';
import { UserService } from '@teammood/shared/util/user';
import { MatButton } from "@angular/material/button";
import { TipService } from "../../../tips/tip.service";
import { TmSlideToggleComponent } from "@teammood/shared/ui/slide-toggle";

@Component({
    selector: 'tm-create-kudos-dialog',
    templateUrl: 'create-kudos-dialog.component.html',
    styleUrls: ['./create-kudos-dialog.component.scss', './../../../timeline-item.scss', './../../../../timeline-item-creation-dialog/timeline-item-creation-dialog.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true },
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        TranslateModule,
        MatStepperModule,
        KudosRecipientsInputListComponent,
        TmMoodPickersComponent,
        MatSelect,
        MatOption,
        KudosPreviewComponent,
        ReactiveFormsModule,
        FormsModule,
        NgClass,
        TemplateListComponent,
        MatButton,
        TmSlideToggleComponent,
    ]
})
export class CreateKudosDialogComponent implements OnInit {

    protected kudos: Kudos;
    protected userInfos: UserInfos;
    protected kudosContentTemplates: string[];
    protected stepperOrientation: Observable<StepperOrientation>;

    protected dontShowAgainCtrl = new FormControl<boolean>(false);
    protected teamsCtrl = new FormControl('', [Validators.required]);
    protected kudosMessageCtrl = new FormControl<string>('', [Validators.required, Validators.minLength(1)]);
    protected kudosRecipientsFormGroup = new FormGroup({
        allTeamSelectedCtrl: new FormControl<boolean>(false, [Validators.requiredTrue]),
        selectedTeammatesCtrl: new FormControl<KudosRecipient[]>([], [Validators.required, Validators.minLength(1)]),
    });
    protected togglePrivacyCtrl = new FormControl<boolean>(false)

    protected showIntro: boolean = true;
    
    get allTeamSelectedCtrl() {
        return this.kudosRecipientsFormGroup.get('allTeamSelectedCtrl');
    }
    get selectedTeammatesCtrl() {
        return this.kudosRecipientsFormGroup.get('selectedTeammatesCtrl');
    }

    protected chooseFormGroup = new FormGroup({
        kudosTypeCtrl: new FormControl<KudosType>(null, [Validators.required]),
        recipientsCtrl: new FormControl<boolean>(false, [Validators.requiredTrue]),
    })
    get typeCtrl() {
        return this.chooseFormGroup.get('kudosTypeCtrl');
    }

    get recipientsCtrl() {
        return this.chooseFormGroup.get('recipientsCtrl');
    }

    private _destroy$ = new Subject<void>();


    constructor(
        @Inject(MAT_DIALOG_DATA) protected dialogInput: CreateKudosDialogData,
        private dialogRef: MatDialogRef<CreateKudosDialogComponent, Kudos>,
        private userActivityService: UserActivityService,
        private translate: TranslateService,
        private userService: UserService,
        private tipService: TipService,
    ) {
        const breakpointObserver = inject(BreakpointObserver);
        this.stepperOrientation = breakpointObserver
          .observe('(min-width: 800px)')
          .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));
    }

    ngOnInit() {
        this.kudos = new Kudos(this.dialogInput.author);

        this.userService.get().then(async (user) => {
            if (user) {
                this.userInfos = new UserInfos(user.firstname, user.lastname);

                if (user.dismissedTips.includes('kudos-intro-tips')) {
                    this.showIntro = false;
                }
            }
        })

        this.dontShowAgainCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            mergeMap(dontShowAgain => {
                if (dontShowAgain) {
                    return this.tipService.dismissTipsById('kudos-intro-tips');
                } else {
                    return this.tipService.restoreTips('kudos-intro-tips');
                }
            }),
            takeUntil(this.dialogRef.afterClosed())
        ).subscribe();

        this.teamsCtrl.valueChanges.pipe(
            tap(teamId => {
                this.kudos.teamId = teamId;
            }),
            takeUntil(this.dialogRef.afterClosed())
        ).subscribe();

        if(this.dialogInput.teams.length === 1) {
            this.teamsCtrl.setValue(this.dialogInput.teams[0].id, {emitEvent: true});
        }

        this.typeCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap(kudosType => {
                this.kudos.kudosType = kudosType;
                this.kudosContentTemplates = this.getKudosContentTemplates(kudosType);
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.kudosMessageCtrl.valueChanges.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            tap(() => {
                this.kudos.message = {
                    body: this.kudosMessageCtrl.getRawValue(),
                    linkPreviews: []
                };
            }),
        ).subscribe();

        this.allTeamSelectedCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap(() => {
                this.kudos.recipients.allTeamSelected = this.allTeamSelectedCtrl.getRawValue();
                this.recipientsCtrl.setValue(this.kudos.recipients.allTeamSelected || this.kudos.recipients.selectedTeammates.length > 0);
            }),
        ).subscribe();
        this.selectedTeammatesCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap(() => {
                this.kudos.recipients.selectedTeammates = this.selectedTeammatesCtrl.getRawValue();
                this.recipientsCtrl.setValue(this.kudos.recipients.allTeamSelected || this.kudos.recipients.selectedTeammates.length > 0);
            }),
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected onTeammatesListChange(recipients: KudosRecipients) {
        this.allTeamSelectedCtrl.setValue(recipients.allTeamSelected);
        this.selectedTeammatesCtrl.setValue(recipients.selectedTeammates);
    }

    protected updateKudosType(kudosType: KudosType) {
        this.typeCtrl.setValue(kudosType);
    }

    private getKudosContentTemplates(kudosType: KudosType) {
        return this.userActivityService
            .getKudosTemplates()
            .filter(templates => templates.type === kudosType)
            .flatMap(templates => templates.templates)
            .map(translationKey => this.translate.instant(translationKey));
    }

    protected updatePlaceholder(placeholder: string){
        this.kudosMessageCtrl.setValue(placeholder);
    }

    protected clearKudosMessage(){
        this.kudosMessageCtrl.setValue('', {emitEvent: true});
    }

    protected fromMoodPicker(emojiOrGif: any){
        this.kudosMessageCtrl.setValue(this.kudos.message.body + emojiOrGif);
    }

    protected createKudos() {
        this.dialogRef.close(this.kudos);
    }

    protected onClose() {
        this.dialogRef.close();
    }

    private setFocus(targetInput: string) {
        setTimeout(function waitTargetElem() {
            let targetElem = document.getElementById(targetInput);
          if (document.body.contains(targetElem)) {
            targetElem.focus();
          } else {
            setTimeout(waitTargetElem, 100);
          }
        }, 300);
    }

    onChange(event: any) {
    let index = event.selectedIndex;
    if(index === 1){
        this.setFocus('kudos-input');
    }
    }

    protected togglePrivacy() {
        if (this.kudos.privacy === 'public') {
            this.kudos.privacy = 'private';
        } else {
            this.kudos.privacy = 'public';
        }
    }
}

export interface CreateKudosDialogData {
    teams: ITeam[],
    author: Author
}